<template>
  <v-card class="pa-4 elevation-3 rounded-lg mt-5">
    <v-card-title class="text-h6 font-weight-medium text-center mb-4">
      Upload File to Firebase Storage
    </v-card-title>
    <v-card-text>
      <div class="text-center mb-4">
        <p v-if="authStore.isAuthenticated" class="text-caption text-success">
          Logged in as: {{ authStore.user?.email || "N/A" }}
        </p>
        <p v-else class="text-caption text-error">
          Not logged in. Please sign in to upload files.
        </p>
      </div>

      <v-file-input
        label="Select File"
        density="compact"
        variant="outlined"
        show-size
        prepend-icon="mdi-paperclip"
        v-model="selectedFile"
        clearable
        :disabled="!authStore.isAuthenticated || isUploading"
      ></v-file-input>

      <v-progress-linear
        v-if="isUploading"
        :model-value="uploadProgress"
        height="10"
        color="primary"
        class="mb-2 rounded-pill"
        striped
      ></v-progress-linear>
      <div v-if="isUploading" class="text-center text-caption mb-2">
        {{ uploadProgress.toFixed(0) }}% complete
      </div>

      <v-btn
        color="primary"
        class="mt-4"
        @click="uploadFile"
        :disabled="!selectedFile || !authStore.isAuthenticated || isUploading"
        block
        size="large"
        rounded="lg"
      >
        <v-icon start>mdi-cloud-upload</v-icon>
        Upload to Storage
      </v-btn>

      <v-alert
        v-if="uploadStatus"
        :type="uploadStatus.includes('success') ? 'success' : 'error'"
        class="mt-4 rounded-lg"
        density="compact"
        text
        closable
      >
        <span v-html="uploadStatus"></span>
      </v-alert>

      <v-alert
        v-if="!authStore.isAuthenticated"
        type="warning"
        class="mt-4 rounded-lg"
        density="compact"
        text
      >
        You need to be logged in to upload files.
      </v-alert>
    </v-card-text>
  </v-card>

  <v-card class="mt-8 pa-4 elevation-3 rounded-lg">
    <v-card-title class="text-h6 font-weight-medium mb-2">
      File Upload Log (from Firestore)
    </v-card-title>
    <v-card-text>
      <v-btn
        color="info"
        class="mb-4 rounded-lg"
        @click="fetchUploadLogs"
        :loading="isFetchingLogs"
        :disabled="!authStore.isAuthenticated"
        variant="tonal"
      >
        <v-icon start>mdi-refresh</v-icon>
        Refresh Log List
      </v-btn>

      <v-data-table
        :headers="fileHeaders"
        :items="uploadLogs"
        :loading="isFetchingLogs"
        class="elevation-1 rounded-lg"
        item-key="id"
        no-data-text="No file upload logs found. Upload a file to see it here!"
      >
        <template v-slot:item.timestamp="{ item }">
          {{ formatTimestamp(item.timestamp) }}
        </template>
        <template v-slot:item.size="{ item }">
          {{ formatBytes(item.size) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <!-- Changed from downloadURL to actions -->
          <!-- View File in Drive (original functionality) -->
          <v-btn
            v-if="item.downloadURL"
            icon
            small
            :href="item.downloadURL"
            target="_blank"
            color="blue"
            variant="text"
            class="mr-1"
          >
            <v-icon>mdi-download</v-icon>
            <!-- Changed icon to download for direct link -->
            <v-tooltip activator="parent" location="top"
              >Download File</v-tooltip
            >
          </v-btn>

          <!-- New: View Image in Dialog (only for image files) -->
          <v-btn
            v-if="
              item.mimeType &&
              item.mimeType.startsWith('image/') &&
              item.downloadURL
            "
            icon
            small
            color="green"
            variant="text"
            @click="showImageDialog(item.downloadURL)"
          >
            <v-icon>mdi-image-search</v-icon>
            <v-tooltip activator="parent" location="top">View Image</v-tooltip>
          </v-btn>
          <span v-else-if="!item.downloadURL">N/A</span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>

  <!-- Image Dialog -->
  <v-dialog v-model="dialogVisible" max-width="800px">
    <v-card>
      <v-card-title class="headline">
        Image Preview
        <v-spacer></v-spacer>
        <v-btn icon @click="dialogVisible = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="text-center">
        <v-img
          :src="currentImageUrl"
          alt="Uploaded Image Preview"
          contain
          max-height="600px"
          class="mx-auto"
        ></v-img>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="dialogVisible = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";

// Import Firebase Storage and Firestore functions
import { storage, db } from "@/firebaseConfig";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";

const authStore = useAuthStore();

// --- Upload related reactive states ---
const selectedFile = ref(null);
const uploadStatus = ref("");
const isUploading = ref(false);
const uploadProgress = ref(0);

// --- Log related reactive states ---
const isFetchingLogs = ref(false);
const uploadLogs = ref([]);

// --- Dialog related reactive states ---
const dialogVisible = ref(false);
const currentImageUrl = ref("");

// Headers for the v-data-table
const fileHeaders = [
  { title: "Name", key: "fileName", sortable: true },
  { title: "Type", key: "mimeType", sortable: true },
  { title: "Size", key: "size", sortable: true },
  { title: "Upload by", key: "uploadedByEmail", sortable: true },
  { title: "Time", key: "timestamp", sortable: true },
  { title: "Actions", key: "actions", sortable: false }, // Key for the actions slot
];

// --- File Upload Logic for Firebase Storage ---
const uploadFile = async () => {
  if (!selectedFile.value) {
    uploadStatus.value = "Please select a file first.";
    return;
  }
  if (!authStore.isAuthenticated || !authStore.user?.uid) {
    uploadStatus.value = "You must be logged in to upload files.";
    return;
  }

  isUploading.value = true;
  uploadStatus.value = "Starting upload...";
  uploadProgress.value = 0;

  const file = selectedFile.value;
  const fileNameInStorage = `${Date.now()}_${file.name}`;
  const uploaderUid = authStore.user.uid;
  const uploaderEmail = authStore.user.email || "N/A";

  const fileStorageRef = storageRef(
    storage,
    `uploads/Document/${uploaderUid}/${fileNameInStorage}`
  );

  const uploadTask = uploadBytesResumable(fileStorageRef, file);

  uploadTask.on(
    "state_changed",
    (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      uploadProgress.value = progress;
      uploadStatus.value = `Upload is ${progress.toFixed(0)}% done`;
    },
    (error) => {
      console.error("Error during upload:", error);
      uploadStatus.value = `Upload failed: ${error.message}`;
      isUploading.value = false;
    },
    async () => {
      console.log("Upload complete!");
      try {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        console.log("File available at", downloadURL);
        uploadStatus.value = `Upload successful! File: ${file.name}. <a href="${downloadURL}" target="_blank">View File</a>`;

        await addDoc(collection(db, "file_upload_logs"), {
          fileName: file.name,
          storagePath: `uploads/Document/${uploaderUid}/${fileNameInStorage}`,
          downloadURL: downloadURL,
          uploadedByUid: uploaderUid,
          uploadedByEmail: uploaderEmail,
          mimeType: file.type || "application/octet-stream",
          size: file.size,
          timestamp: serverTimestamp(),
        });
        console.log("Upload log saved to Firestore!");

        selectedFile.value = null;
        await fetchUploadLogs(); // Refresh the log list after a successful upload
      } catch (error) {
        console.error("Error getting download URL or saving log:", error);
        uploadStatus.value = `Upload successful, but failed to get download URL or save log: ${error.message}`;
      } finally {
        isUploading.value = false;
        uploadProgress.value = 0;
      }
    }
  );
};

// --- Fetch Upload Logs from Firestore ---
const fetchUploadLogs = async () => {
  if (!authStore.isAuthenticated) {
    console.warn("User not authenticated. Cannot fetch logs.");
    uploadLogs.value = [];
    return;
  }

  isFetchingLogs.value = true;
  uploadLogs.value = [];

  try {
    const logsCollection = collection(db, "file_upload_logs");
    const q = query(logsCollection, orderBy("timestamp", "desc"));
    const querySnapshot = await getDocs(q);

    const fetchedLogs = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      fetchedLogs.push({
        id: doc.id,
        ...data,
        timestamp: data.timestamp ? data.timestamp.toDate() : null,
      });
    });
    uploadLogs.value = fetchedLogs;
    console.log("Fetched Upload Logs:", uploadLogs.value);
  } catch (error) {
    console.error("Error fetching upload logs:", error);
  } finally {
    isFetchingLogs.value = false;
  }
};

// --- Watch for authenticated status to fetch logs initially ---
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      fetchUploadLogs();
    } else {
      uploadLogs.value = [];
    }
  },
  { immediate: true }
);

// --- Dialog Functions ---
const showImageDialog = (imageUrl) => {
  currentImageUrl.value = imageUrl;
  dialogVisible.value = true;
  console.log(imageUrl);
};

// --- Utility functions ---
const formatTimestamp = (dateObject) => {
  if (!dateObject) return "";
  if (!(dateObject instanceof Date)) {
    dateObject = new Date(dateObject);
  }
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  };
  return dateObject.toLocaleDateString(undefined, options);
};

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === undefined || bytes === null) return "";
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};
</script>

<style scoped>
/* You can add component-specific styles here */
</style>
